/**
 * NuGuard Frontend - Zod Schemas
 * Type-safe validation for API contracts
 * These schemas match the backend Pydantic models in shared/schemas.py
 */
import { z } from 'zod';

/** Normalize status/phase strings to UPPERCASE at the API boundary */
const upperStatus = z.string().transform(s => s.toUpperCase());

// ============================================================================
// Enums
// ============================================================================

export const RoleSchema = z.enum(['ADMIN', 'EDITOR', 'VIEWER']);
export type Role = z.infer<typeof RoleSchema>;

export const AccountScopeSchema = z.enum(['tenant', 'platform', 'demouser']);
export type AccountScope = z.infer<typeof AccountScopeSchema>;

export const SeveritySchema = z.enum(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO']);
export type Severity = z.infer<typeof SeveritySchema>;

export const PolicyCategorySchema = z.enum(['COMPLIANCE', 'INTERNAL', 'SAFETY']);
export type PolicyCategory = z.infer<typeof PolicyCategorySchema>;
export const PolicyTypeSchema = z.enum(['SYSTEM_DEFINED', 'CUSTOM']);
export type PolicyType = z.infer<typeof PolicyTypeSchema>;

export const AssetTypeSchema = z.enum(['AGENT', 'MODEL', 'GUARDRAIL', 'AUTH', 'TOOL', 'DATASTORE', 'PROMPT', 'PRIVILEGE', 'FRAMEWORK', 'EVAL_SYSTEM', 'MCP_PROVIDER']);
export type AssetType = z.infer<typeof AssetTypeSchema>;

export const RiskLevelSchema = z.enum(['HIGH', 'MEDIUM', 'LOW']);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

export const AttackTypeSchema = z.enum(['PROMPT_INJECTION', 'JAILBREAK', 'PII_LEAKAGE', 'HALLUCINATION']);
export type AttackType = z.infer<typeof AttackTypeSchema>;

export const ScanStatusSchema = z.enum(['PROCESSING', 'COMPLETED', 'FAILED']);
export type ScanStatus = z.infer<typeof ScanStatusSchema>;

/**
 * Gap type classification - determines severity mapping:
 * - COMPLIANCE -> CRITICAL severity
 * - SECURITY -> HIGH severity  
 * - AI_SAFETY -> MEDIUM severity
 * - BUSINESS_RULES -> MEDIUM severity
 * - BEST_PRACTICE -> LOW severity
 * - PARTIAL -> LOW severity (partial implementation, needs verification)
 */
export const GapTypeSchema = z.enum(['COMPLIANCE', 'SECURITY', 'AI_SAFETY', 'BUSINESS_RULES', 'BEST_PRACTICE', 'PARTIAL', 'MISSING_CONTROL']);
export type GapType = z.infer<typeof GapTypeSchema>;

/**
 * Scan progress phase - used for real-time status tracking
 */
export const ScanPhaseSchema = z.enum([
  'INITIATED',
  'FETCHING_REPO',
  'DISCOVERING_ASSETS',
  'SAVING_RESULTS',
  'DISCOVERY_COMPLETE',
  'ANALYZING_GAPS',
  'GENERATING_ATTACKS',
  'COMPLETED',
  'FAILED'
]);
export type ScanPhase = z.infer<typeof ScanPhaseSchema>;

/**
 * Risk band based on risk_score (0-100):
 * - LOW: 0-25
 * - MODERATE: 26-50
 * - HIGH: 51-80
 * - CRITICAL: 81-100
 */
export const RiskBandSchema = z.enum(['LOW', 'MODERATE', 'HIGH', 'CRITICAL']);
export type RiskBand = z.infer<typeof RiskBandSchema>;

/**
 * Type of scan target
 */
export const TargetTypeSchema = z.enum(['GITHUB_REPO', 'DESCRIPTION', 'FILE_UPLOAD']);
export type TargetType = z.infer<typeof TargetTypeSchema>;

// ============================================================================
// Auth Schemas
// ============================================================================

export const LoginRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const TokenResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  token_type: z.string().default('bearer'),
  expires_in: z.number(),
});
export type TokenResponse = z.infer<typeof TokenResponseSchema>;

// ============================================================================
// User Schemas
// ============================================================================

export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().min(1).max(255),
  role: RoleSchema,
  scope: AccountScopeSchema.optional(),
  avatar: z.string().nullable().optional(),
  tenant_name: z.string().nullable().optional(),
  tenant_id: z.string(),
  is_active: z.boolean(),
  created_at: z.string(),
  credits_balance: z.number().nullable().optional(),
  credits_included: z.number().nullable().optional(),
  subscription_status: z.string().nullable().optional(),
  billing_period_end: z.string().nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const UserCreateSchema = z.object({
  email: z.string().email('Invalid email address'),
  name: z.string().min(1, 'Name is required').max(255),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  role: RoleSchema.default('VIEWER'),
  avatar: z.string().url().optional(),
});
export type UserCreate = z.infer<typeof UserCreateSchema>;

export const UserUpdateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  role: RoleSchema.optional(),
  avatar: z.string().url().nullable().optional(),
  password: z.string().min(6).optional(),
});
export type UserUpdate = z.infer<typeof UserUpdateSchema>;

// ============================================================================
// Application Schemas
// ============================================================================

export const ApplicationSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  repository_url: z.string().nullable().optional(),
  tenant_id: z.string(),
  created_at: z.string(),
  
  // User who created the application
  created_by_user_id: z.string().nullable().optional(),
  created_by_user_name: z.string().nullable().optional(),
  created_by_user_email: z.string().nullable().optional(),
  
  // Enriched Metrics
  latest_scan_id: z.string().nullable().optional(),
  latest_scan_coverage: z.number().nullable().optional(),
  latest_scan_policy_count: z.number().nullable().optional(),
});
export type Application = z.infer<typeof ApplicationSchema>;

export const ApplicationCreateSchema = z.object({
  name: z.string().min(1, 'Application name is required').max(255),
  description: z.string().optional(),
});
export type ApplicationCreate = z.infer<typeof ApplicationCreateSchema>;

// ============================================================================
// Policy Schemas
// ============================================================================

/**
 * Control response schema - defines a compliance control from a regulatory framework.
 * Used within PolicySchema for nested controls array.
 */
export const PolicyControlSchema = z.object({
  id: z.string(),
  control_id: z.string(),  // Framework-specific ID (e.g., "1.1.1", "Art.5(1)(a)")
  // ControlLibrary uses "title"; legacy "name" accepted for backward compat
  title: z.string().optional(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  framework: z.string().optional(),
  version: z.string().optional(),
  severity: z.string().optional(),
  control_type: z.string().optional(),
  enabled: z.boolean().optional(),
  ai_applicable: z.boolean().optional(),
  weight: z.number().optional(),
  fail_threshold: z.number().optional(),
  partial_threshold: z.number().optional(),
  typical_gaps: z.array(z.string()).nullable().optional(),
  typical_mitigations: z.array(z.string()).nullable().optional(),
  mitre_atlas_techniques: z.array(z.object({
    technique_id: z.string(),
    technique_name: z.string().optional(),
    atlas_url: z.string().optional(),
  })).nullable().optional(),
  llm_context: z.record(z.any()).nullable().optional(),
  tags: z.array(z.string()).nullable().optional(),
  metadata_tags: z.array(z.string()).nullable().optional(),
  applicable_asset_types: z.array(z.string()).nullable().optional(),
  graph_query: z.record(z.any()).nullable().optional(),
  assertions: z.array(z.any()).nullable().optional(),
  source_ref: z.string().nullable().optional(),
  policy_id: z.string().nullable().optional(),
  tenant_id: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  created_by: z.string().nullable().optional(),
}).transform((c) => ({
  ...c,
  // Ensure "name" is always populated (ControlLibrary sends "title")
  name: c.name || c.title || '',
  // Ensure "ai_applicable" is populated from "enabled" for backward compat
  ai_applicable: c.ai_applicable ?? c.enabled ?? true,
}));
export type PolicyControl = z.infer<typeof PolicyControlSchema>;

export const PolicySchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  content: z.string().min(1),
  enabled: z.boolean(),
  category: PolicyCategorySchema,
  policy_type: PolicyTypeSchema.optional(),
  is_custom: z.boolean(),
  tenant_id: z.string().nullable().optional(),
  created_by: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
  controls: z.array(PolicyControlSchema).optional().default([]),
  controls_extraction_status: z.enum(['pending', 'ready', 'failed']).nullable().optional(),
});
export type Policy = z.infer<typeof PolicySchema>;

export const PolicyCreateSchema = z.object({
  name: z.string().min(1, 'Policy name is required').max(255),
  description: z.string().optional(),
  content: z.string().min(1, 'Policy content is required'),
  enabled: z.boolean().default(true),
  category: PolicyCategorySchema,
});
export type PolicyCreate = z.infer<typeof PolicyCreateSchema>;

export const PolicyUpdateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  content: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  category: PolicyCategorySchema.optional(),
});
export type PolicyUpdate = z.infer<typeof PolicyUpdateSchema>;

// ============================================================================
// Control Schemas (Policy Controls)
// ============================================================================

/**
 * Compliance control from a regulatory framework
 */
export const ControlSchema = z.object({
  id: z.string(),
  control_id: z.string(),  // Framework-specific ID (e.g., "1.1.1", "Art.5(1)(a)")
  name: z.string(),
  description: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  ai_applicable: z.boolean().catch(true),
  typical_gaps: z.array(z.string()).nullable().optional(),
  typical_mitigations: z.array(z.string()).nullable().optional(),
  llm_context: z.record(z.any()).nullable().optional(),
  metadata_tags: z.array(z.string()).nullable().optional(),
  source_ref: z.string().nullable().optional(),
  policy_id: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
});
export type Control = z.infer<typeof ControlSchema>;

// ============================================================================
// Finding Schemas
// ============================================================================

/**
 * A single piece of evidence for a finding
 */
export const EvidenceItemSchema = z.object({
  file_path: z.string().nullable().optional(),
  line_number: z.number().int().nullable().optional(),
  code_snippet: z.string().nullable().optional(),
  description: z.string(),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

/**
 * Finding with gap analysis fields for detailed compliance reporting
 */
export const FindingSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  severity: SeveritySchema,
  
  // Gap classification fields (from gap analysis)
  gap_type: GapTypeSchema.nullable().optional(),
  control_id: z.string().nullable().optional(),
  control_name: z.string().nullable().optional(),
  control_ref_id: z.string().nullable().optional(),
  control_ref: z.object({
    id: z.string(),
    control_id: z.string(),
    name: z.string(),
    description: z.string(),
    policy_id: z.string(),
  }).nullable().optional(),
  policy_name: z.string().nullable().optional(),  // Policy this finding belongs to
  
  // Location (primary occurrence)
  affected_file: z.string().nullable().optional(),
  line_number: z.number().int().nullable().optional(),
  
  // Remediation and evidence
  remediation: z.string(),
  evidence: z.string().nullable().optional(),
  evidences: z.array(EvidenceItemSchema).nullable().optional(),
  evidence_count: z.number().int().nullable().optional(),
  confidence_score: z.number().int().min(0).max(100).nullable().optional(),
  
  // Partial coverage indicator (control has some evidence but below threshold)
  partial_coverage: z.boolean().nullable().optional(),
  // Three-phase pipeline indicators (optional, present on enriched assessments)
  aibom_evidence_count: z.number().nullable().optional(),
  repo_hits_count: z.number().nullable().optional(),
});
export type Finding = z.infer<typeof FindingSchema>;

// ============================================================================
// Red Team Attack Schemas
// ============================================================================

export const RedTeamAttackSchema = z.object({
  id: z.string(),
  attack_type: AttackTypeSchema,
  prompt: z.string(),
  expected_outcome: z.string(),
  success_likelihood: RiskLevelSchema,
});
export type RedTeamAttack = z.infer<typeof RedTeamAttackSchema>;

// ============================================================================
// AI Asset Schemas
// ============================================================================

export const AIAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: AssetTypeSchema,
  description: z.string().nullable().optional(),
  risk_level: RiskLevelSchema,
  file_path: z.string().nullable().optional(),  // Path to source file
  line_start: z.number().nullable().optional(), // Starting line number
  line_end: z.number().nullable().optional(),   // Ending line number
  details: z.string().nullable().optional(),     // Detailed information (e.g., full content)
  framework: z.string().nullable().optional(),   // Framework (e.g., langchain, langgraph)
  confidence: z.number().nullable().optional(),  // Confidence score (0-1)
  evidence_sources: z.array(z.string()).nullable().optional(), // Evidence source types
  additional_evidence: z.array(z.object({
    file_path: z.string().optional(),
    line_start: z.number().optional(),
    line_end: z.number().optional(),
    snippet: z.string().optional(),
  })).nullable().optional(),
  application_id: z.string(),
  tenant_id: z.string(),
  created_at: z.string().nullable().optional().default(new Date().toISOString()),
});
export type AIAsset = z.infer<typeof AIAssetSchema>;

// ============================================================================
// Scan Schemas
// ============================================================================


/**
 * Risk summary from gap analysis
 */
export const RiskSummarySchema = z.object({
  critical_count: z.number().int().default(0),
  high_count: z.number().int().default(0),
  medium_count: z.number().int().default(0),
  low_count: z.number().int().default(0),
  top_risk_areas: z.array(z.string()).default([]),
  recommendations: z.array(z.string()).default([]),
});
export type RiskSummary = z.infer<typeof RiskSummarySchema>;

/**
 * Evidence item for covered controls
 */
export const CoveredControlEvidenceItemSchema = z.object({
  file_path: z.string().nullable().optional(),
  line_number: z.number().int().nullable().optional(),
  code_snippet: z.string().nullable().optional(),
  description: z.string(),
});
export type CoveredControlEvidenceItem = z.infer<typeof CoveredControlEvidenceItemSchema>;

export const ControlRefSummarySchema = z.object({
  id: z.string(),
  control_id: z.string(),
  name: z.string(),
  description: z.string(),
  policy_id: z.string(),
});
export type ControlRefSummary = z.infer<typeof ControlRefSummarySchema>;

/**
 * Covered control with audit-ready evidence
 */
export const CoveredControlSchema = z.object({
  id: z.string(),
  control_id: z.string(),
  control_name: z.string().nullable().optional(),
  control_ref_id: z.string().nullable().optional(),
  control_ref: ControlRefSummarySchema.nullable().optional(),
  policy_name: z.string().nullable().optional(),
  evidence_type: z.enum(['CODE', 'CONFIG', 'DOCUMENTATION', 'ARCHITECTURE']).nullable().optional(),
  evidence_summary: z.string().nullable().optional(),
  evidence_details: z.array(CoveredControlEvidenceItemSchema).default([]),
  confidence_score: z.number().int().min(0).max(100).default(80),
  verification_method: z.string().nullable().optional(),
  scan_id: z.string(),
  /** True when the LLM assessed this control as PARTIAL (some evidence found, gaps remain) */
  partial: z.boolean().optional().default(false),
  /** True when control is attestation-driven (ORGANIZATIONAL/VENDOR/PHYSICAL) and has no approved attestation yet */
  needs_attestation: z.boolean().optional().default(false),
});
export type CoveredControl = z.infer<typeof CoveredControlSchema>;

/**
 * Full scan response with all details
 * 
 * Uses .catch() for defaults to ensure proper TypeScript inference
 * where input can be missing but output always has a value.
 */
export const ScanResponseSchema = z.object({
  id: z.string(),
  target_name: z.string(),
  
  // Target info - use .catch() for proper type inference
  target_type: TargetTypeSchema.catch('DESCRIPTION'),
  github_url: z.string().nullable().optional(),
  github_branch: z.string().nullable().optional(),
  github_commit_sha: z.string().nullable().optional(),
  
  // Phase tracking
  phase: upperStatus.catch('COMPLETED'),
  phase_message: z.string().nullable().optional(),

  // Risk scoring (higher = more risk)
  risk_score: z.number().int().min(0).max(100).catch(0),
  risk_band: RiskBandSchema.catch('LOW'),

  // Legacy field for backwards compatibility
  compliance_score: z.number().int().min(0).max(100).optional(),

  status: upperStatus,
  application_id: z.string(),
  application_name: z.string(),
  tenant_id: z.string(),
  
  // User who initiated the scan
  initiated_by_user_id: z.string().nullable().optional(),
  initiated_by_user_name: z.string().nullable().optional(),
  initiated_by_user_email: z.string().nullable().optional(),
  
  // Counts
  assets_discovered_count: z.number().int().catch(0),
  findings_count: z.number().int().catch(0),
  policies_analyzed: z.array(z.string()).nullable().optional(),
  
  // Application summary (from AI analysis)
  deployment_type: z.string().nullable().optional(),  // PUBLIC_FACING, INTERNAL
  industry_sector: z.string().nullable().optional(),  // HEALTHCARE, FINANCIAL, etc.
  modalities: z.array(z.string()).nullable().optional(),  // ["TEXT", "VOICE", etc.]
  use_case_summary: z.string().nullable().optional(),  // Brief description
  
  // Related data
  findings: z.array(FindingSchema),
  covered_controls: z.array(CoveredControlSchema).default([]),
  red_team_attacks: z.array(RedTeamAttackSchema),
  detected_assets: z.array(AIAssetSchema),
  
  // Risk summary
  risk_summary: RiskSummarySchema.nullable().optional(),
  
  created_at: z.string(),
});
export type ScanResponse = z.infer<typeof ScanResponseSchema>;

/**
 * Lightweight scan for list views
 * 
 * Uses .catch() for defaults to ensure proper TypeScript inference.
 */
export const ScanListItemSchema = z.object({
  id: z.string(),
  target_name: z.string(),
  target_type: TargetTypeSchema.catch('DESCRIPTION'),
  risk_score: z.number().int().min(0).max(100).catch(0),
  risk_band: RiskBandSchema.catch('LOW'),
  // Legacy field for backwards compatibility
  compliance_score: z.number().int().optional(),
  status: upperStatus,
  phase: upperStatus.catch('COMPLETED'),
  application_id: z.string().nullable().optional(),
  application_name: z.string(),
  findings_count: z.number().int(),
  // Covered controls count for coverage calculation
  covered_controls_count: z.number().int().catch(0),
  // Policies that were assessed (null for discovery-only scans)
  policies_analyzed: z.array(z.string()).nullable().optional(),
  // User who initiated the scan
  initiated_by_user_id: z.string().nullable().optional(),
  initiated_by_user_name: z.string().nullable().optional(),
  // Risk summary for dashboard aggregation
  risk_summary: RiskSummarySchema.nullable().optional(),
  // Application summary (from AI analysis)
  deployment_type: z.string().nullable().optional(),
  industry_sector: z.string().nullable().optional(),
  modalities: z.array(z.string()).nullable().optional(),
  use_case_summary: z.string().nullable().optional(),
  created_at: z.string(),
});
export type ScanListItem = z.infer<typeof ScanListItemSchema>;

/**
 * Real-time scan status for progress tracking
 * 
 * Uses .catch() for defaults to ensure proper TypeScript inference.
 */
export const ScanStatusResponseSchema = z.object({
  id: z.string(),
  status: upperStatus,
  phase: upperStatus,
  phase_message: z.string().nullable().optional(),
  assets_discovered_count: z.number().int().catch(0),
  findings_count: z.number().int().catch(0),
  risk_score: z.number().int().min(0).max(100).catch(0),
  risk_band: RiskBandSchema.catch('LOW'),
});
export type ScanStatusResponse = z.infer<typeof ScanStatusResponseSchema>;

// ============================================================================
// Audit Log Schemas
// ============================================================================

export const AuditLogSchema = z.object({
  id: z.string(),
  action: z.string(),
  details: z.string().nullable().optional(),
  user_id: z.string(),
  user_name: z.string(),
  created_at: z.string(),
  resource_type: z.string().nullable().optional(),
  resource_id: z.string().nullable().optional(),
  application_id: z.string().nullable().optional(),
  application_name: z.string().nullable().optional(),
  scan_id: z.string().nullable().optional(),
});
export type AuditLog = z.infer<typeof AuditLogSchema>;

// ============================================================================
// API Response Wrappers
// ============================================================================

export const ErrorResponseSchema = z.object({
  detail: z.string(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

export const HealthResponseSchema = z.object({
  status: z.string(),
  service: z.string(),
  version: z.string().optional(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

// ============================================================================
// AIBOM Schemas (ai_asset_service)
// ============================================================================

export const AIBOMScanStatusSchema = z.enum([
  'PENDING', 'PROCESSING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED'
]);
export type AIBOMScanStatus = z.infer<typeof AIBOMScanStatusSchema>;

export const TestRunStatusSchema = z.enum([
  'pending', 'running', 'synthesizing', 'completed', 'failed'
]);
export type TestRunStatus = z.infer<typeof TestRunStatusSchema>;

export const NodeTypeSchema = z.enum([
  'MODEL', 'TOOL', 'AGENT', 'PROMPT', 'DATASTORE', 'AUTH',
  'CREDENTIAL', 'GUARDRAIL', 'PRIVILEGE', 'FRAMEWORK', 'UNKNOWN'
]);
export type NodeType = z.infer<typeof NodeTypeSchema>;

export const EdgeTypeSchema = z.enum([
  'USES', 'CALLS', 'RETRIEVES_FROM', 'AUTHENTICATES_WITH',
  'HAS_ACCESS_TO', 'ORCHESTRATES', 'DELEGATES_TO', 'PROTECTS',
  'UNKNOWN'
]);
export type EdgeType = z.infer<typeof EdgeTypeSchema>;

export const AIBOMScanCreateSchema = z.object({
  application_id: z.string(),
  source_type: z.string(),
  source_ref: z.string(),
  branch: z.string().optional().nullable(),
  commit_sha: z.string().optional().nullable(),
  scan_id: z.string().optional().nullable(),
  github_token: z.string().optional().nullable(),
  is_rescan: z.boolean().optional(),
  force_rescan: z.boolean().optional(),
});
export type AIBOMScanCreate = z.infer<typeof AIBOMScanCreateSchema>;

export const AIBOMScanResponseSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  application_id: z.string(),
  scan_id: z.string().nullable().optional(),
  target_type: z.string().nullable().optional(),
  github_url: z.string().nullable().optional(),
  github_branch: z.string().nullable().optional(),
  github_commit_sha: z.string().nullable().optional(),
  status: upperStatus,
  phase: z.string().nullable().optional(),
  phase_message: z.string().nullable().optional(),
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  static_analysis_completed_at: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  node_count: z.number().default(0),
  edge_count: z.number().default(0),
  files_scanned: z.number().default(0),
  created_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  cognitive_results_risk_score: z.number().nullable().optional(),
  initiated_by_user_name: z.string().nullable().optional(),
  // nuguard 0.7.1 / schema 1.5.0
  relationship_graph_md: z.string().nullable().optional(),
  total_loc: z.number().int().nullable().optional(),
  credits_consumed: z.number().nullable().optional(),
});
export type AIBOMScanResponse = z.infer<typeof AIBOMScanResponseSchema>;

export const AIBOMScanListResponseSchema = z.object({
  items: z.array(AIBOMScanResponseSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});
export type AIBOMScanListResponse = z.infer<typeof AIBOMScanListResponseSchema>;

export const AIBOMScanSummaryResponseSchema = z.object({
  aibom_scan_id: z.string().optional(),
  scan_id: z.string().optional(),
  application_id: z.string(),
  github_url: z.string().nullable().optional(),
  github_branch: z.string().nullable().optional(),
  node_type_counts: z.record(z.number()).default({}),
  frameworks: z.array(z.string()).default([]),
  deployment_platforms: z.array(z.string()).default([]),
  subscription_account_project: z.array(z.string()).default([]),
  regions: z.array(z.string()).default([]),
  environments: z.array(z.string()).default([]),
  deployment_urls: z.array(z.string()).default([]),
  api_endpoints: z.array(z.string()).default([]),
  env_var_keys: z.array(z.string()).default([]),
  use_case_summary: z.string().nullable().optional(),
  modalities: z.array(z.string()).default([]),
  modality_support: z.object({
    text: z.boolean().default(true),
    voice: z.boolean().default(false),
    image: z.boolean().default(false),
    video: z.boolean().default(false),
  }).default({ text: true, voice: false, image: false, video: false }),
  // nuguard 0.7.1 / schema 1.5.0 fields
  nuguard_schema_version:      z.string().nullable().optional(),
  iac_security_summary:        z.string().nullable().optional(),
  security_findings:           z.array(z.string()).default([]),
  secret_stores:               z.array(z.string()).default([]),
  availability_zones:          z.array(z.string()).default([]),
  encryption_at_rest_coverage: z.boolean().nullable().optional(),
  relationship_graph_md:       z.string().nullable().optional(),
  total_loc:                   z.number().int().nullable().optional(),
});
export type AIBOMScanSummaryResponse = z.infer<typeof AIBOMScanSummaryResponseSchema>;

export const AIBOMNodeResponseSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  aibom_scan_id: z.string(),
  canonical_id: z.string(),
  node_type: z.string(),
  name: z.string(),
  properties: z.record(z.unknown()),
  labels: z.array(z.string()),
  confidence: z.union([z.number(), z.string()]).transform(v => typeof v === 'string' ? parseFloat(v) : v),
  confidence_rationale: z.array(z.string()),
  status: upperStatus,
  first_seen_at: z.string(),
  last_seen_at: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  // NuGuard primary schema — optional, absent on pre-migration scans
  nuguard_node:   z.record(z.unknown()).optional().nullable(),
  framework:      z.string().optional().nullable(),
  model_name:     z.string().optional().nullable(),
  datastore_type: z.string().optional().nullable(),
  nuguard_node_id: z.string().optional().nullable(),
  // nuguard 0.6.2 expanded SBOM fields
  version:  z.string().optional().nullable(),
  license:  z.string().optional().nullable(),
  iam_type: z.string().optional().nullable(),
  // nuguard 0.7.1 / schema 1.5.0 node fields
  descriptive_name:     z.string().optional().nullable(),
  injection_risk_score: z.number().optional().nullable(),
  no_auth_required:     z.boolean().optional().nullable(),
  high_privilege:       z.boolean().optional().nullable(),
  sql_injectable:       z.boolean().optional().nullable(),
  ssrf_possible:        z.boolean().optional().nullable(),
  idor_surface:         z.boolean().optional().nullable(),
  pii_fields:           z.array(z.string()).optional().nullable(),
  pfi_fields:           z.array(z.string()).optional().nullable(),
  // Optionally embedded evidence (present when the backend inlines it on the node response)
  evidence: z.array(z.object({
    location: z.object({
      path: z.string().optional(),
      line: z.number().optional(),
    }).optional(),
  })).optional(),
});
export type AIBOMNodeResponse = z.infer<typeof AIBOMNodeResponseSchema>;

export const AIBOMNodeListResponseSchema = z.object({
  items: z.array(AIBOMNodeResponseSchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
});
export type AIBOMNodeListResponse = z.infer<typeof AIBOMNodeListResponseSchema>;

export const AIBOMEdgeResponseSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  aibom_scan_id: z.string(),
  source_node_id: z.string(),
  target_node_id: z.string(),
  edge_type: z.string(),
  properties: z.record(z.unknown()),
  confidence: z.union([z.number(), z.string()]).transform(v => typeof v === 'string' ? parseFloat(v) : v),
  confidence_rationale: z.array(z.string()),
  created_at: z.string(),
  // nuguard 0.6.2: NuGuard SBOM native relationship type (USES, CALLS, ORCHESTRATES…)
  // Populated from the dedicated DB column added in migration 051.
  relationship_type: z.string().optional().nullable(),
  nuguard_edge: z.record(z.unknown()).optional().nullable(),
});
export type AIBOMEdgeResponse = z.infer<typeof AIBOMEdgeResponseSchema>;

export const AIBOMEdgeListResponseSchema = z.object({
  items: z.array(AIBOMEdgeResponseSchema),
  total: z.number(),
});
export type AIBOMEdgeListResponse = z.infer<typeof AIBOMEdgeListResponseSchema>;

export const AIBOMEvidenceResponseSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  aibom_scan_id: z.string(),
  node_id: z.string().nullable().optional(),
  edge_id: z.string().nullable().optional(),
  kind: z.string(),
  extractor: z.string().nullable().optional(),
  file_path: z.string().nullable().optional(),
  line_start: z.number().nullable().optional(),
  line_end: z.number().nullable().optional(),
  json_path: z.string().nullable().optional(),
  snippet_hash: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  created_at: z.string(),
});
export type AIBOMEvidenceResponse = z.infer<typeof AIBOMEvidenceResponseSchema>;

export const AIBOMEvidenceListResponseSchema = z.object({
  items: z.array(AIBOMEvidenceResponseSchema),
  total: z.number(),
});
export type AIBOMEvidenceListResponse = z.infer<typeof AIBOMEvidenceListResponseSchema>;

export const AIBOMGraphResponseSchema = z.object({
  // Backend serialises as aibom_scan_id; accept both for backwards compat.
  aibom_scan_id: z.string().optional(),
  scan_id: z.string().optional(),
  application_id: z.string(),
  nodes: z.array(AIBOMNodeResponseSchema),
  edges: z.array(AIBOMEdgeResponseSchema),
  metadata: z.record(z.unknown()).optional(),
});
export type AIBOMGraphResponse = z.infer<typeof AIBOMGraphResponseSchema>;

export const NodeTypeCountSchema = z.object({
  node_type: z.string(),
  count: z.number(),
});
export type NodeTypeCount = z.infer<typeof NodeTypeCountSchema>;

export const PackageDepResponseSchema = z.object({
  name: z.string(),
  version_spec: z.string().default(""),
  purl: z.string().default(""),
  group: z.string().default("runtime"),
  source_file: z.string().default(""),
});
export type PackageDepResponse = z.infer<typeof PackageDepResponseSchema>;

export const ScanDependenciesResponseSchema = z.object({
  scan_id: z.string(),
  total: z.number(),
  deps: z.array(PackageDepResponseSchema),
});
export type ScanDependenciesResponse = z.infer<typeof ScanDependenciesResponseSchema>;

// ============================================================================
// Assessment Schemas (assessment_service)
// ============================================================================

export const ComplianceResultSchema = z.enum([
  'PASS', 'PARTIAL', 'FAIL', 'NOT_APPLICABLE', 'UNABLE_TO_ASSESS'
]);
export type ComplianceResult = z.infer<typeof ComplianceResultSchema>;

export const AssessmentStatusSchema = z.enum([
  'PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED'
]);
export type AssessmentStatus = z.infer<typeof AssessmentStatusSchema>;

export const AttestationStatusSchema = z.enum([
  'PENDING', 'APPROVED', 'REJECTED', 'EXPIRED'
]);
export type AttestationStatus = z.infer<typeof AttestationStatusSchema>;

export const ReportStatusSchema = z.enum([
  'PENDING', 'GENERATING', 'COMPLETED', 'FAILED'
]);
export type ReportStatus = z.infer<typeof ReportStatusSchema>;

export const ControlTypeSchema = z.enum([
  'AI_TECHNICAL', 'APP_TECHNICAL', 'ORGANIZATIONAL',
  'VENDOR', 'PHYSICAL', 'INFRASTRUCTURE'
]);
export type ControlType = z.infer<typeof ControlTypeSchema>;

export const AssessmentCreateSchema = z.object({
  aibom_id: z.string(),
  application_id: z.string(),
  frameworks: z.array(z.string()).min(1),
  assessment_profile: z.string().optional().nullable(),
});
export type AssessmentCreate = z.infer<typeof AssessmentCreateSchema>;

export const AssessmentResponseSchema = z.object({
  id: z.string(),
  aibom_id: z.string(),
  aibom_version: z.string().nullable().optional(),
  application_id: z.string(),
  tenant_id: z.string(),
  frameworks: z.array(z.string()),
  assessment_profile: z.string().nullable().optional(),
  status: upperStatus,
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
  overall_score: z.number().nullable().optional(),
  pass_count: z.number(),
  partial_count: z.number(),
  fail_count: z.number(),
  na_count: z.number(),
  unable_count: z.number(),
  initiated_by: z.string(),
  assessed_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
});
export type AssessmentResponse = z.infer<typeof AssessmentResponseSchema>;

export const AssessmentListResponseSchema = z.object({
  assessments: z.array(AssessmentResponseSchema),
  total: z.number(),
});
export type AssessmentListResponse = z.infer<typeof AssessmentListResponseSchema>;

/** Control library response - must be defined before AssessmentResultResponse */
export const ControlLibraryResponseSchema = z.object({
  id: z.string(),
  control_id: z.string(),
  framework: z.string(),
  version: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  guidance: z.string().nullable().optional(),
  severity: z.string(),
  control_type: z.string(),
  applicable_asset_types: z.array(z.string()).nullable().optional(),
  tags: z.array(z.string()),
  enabled: z.boolean(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
});
export type ControlLibraryResponse = z.infer<typeof ControlLibraryResponseSchema>;

/** Evidence item within a three-phase assessment finding */
export const AssessmentResultEvidenceItemSchema = z.union([
  z.string(),
  z.object({ ref: z.string(), note: z.string() }),
]);
export type AssessmentResultEvidenceItem = z.infer<typeof AssessmentResultEvidenceItemSchema>;

/** Assessment result finding (sub-object within result) */
export const AssessmentResultFindingSchema = z.object({
  // Legacy fields
  message: z.string().optional().default(''),
  node_id: z.string().nullable().optional(),
  node_type: z.string().nullable().optional(),
  severity: z.string().nullable().optional(),
  // Three-phase enriched fields (pipeline: "three_phase")
  title: z.string().nullable().optional(),
  status: z.enum(['GAP', 'COVERED', 'PARTIAL']).nullable().optional(),
  evidence_summary: z.string().nullable().optional(),
  // Backend legacy findings return evidence=null; three-phase findings return evidence=[...]
  evidence: z.array(AssessmentResultEvidenceItemSchema).nullish().transform(v => v ?? []),
  aibom_evidence_count: z.number().nullable().optional(),
  repo_hits_count: z.number().nullable().optional(),
  inventory_coverage_level: z.string().nullable().optional(),
  pipeline: z.string().nullable().optional(),
  // snapshot metadata (kept for backward compat)
  snapshot_cached: z.boolean().nullable().optional(),
  cache_hit: z.boolean().nullable().optional(),
});
export type AssessmentResultFinding = z.infer<typeof AssessmentResultFindingSchema>;

/** Assessment result response - matches backend AssessmentResultResponse */
export const AssessmentResultResponseSchema = z.object({
  id: z.string(),
  assessment_id: z.string(),
  control_id: z.string().nullable().optional(),
  result: ComplianceResultSchema,
  score: z.number(),
  confidence: z.number().default(1.0),
  findings: z.array(AssessmentResultFindingSchema).default([]),
  rationale: z.string().nullable().optional(),
  remediation: z.string().nullable().optional(),
  matched_nodes: z.array(z.string()).default([]),
  is_overridden: z.boolean().default(false),
  override_reason: z.string().nullable().optional(),
  overridden_by: z.string().nullable().optional(),
  overridden_at: z.string().nullable().optional(),
  original_result: ComplianceResultSchema.nullable().optional(),
  original_score: z.number().nullable().optional(),
  evaluated_at: z.string(),
  control: ControlLibraryResponseSchema.nullable().optional(),
});
export type AssessmentResultResponse = z.infer<typeof AssessmentResultResponseSchema>;

export const ControlListResponseSchema = z.object({
  controls: z.array(ControlLibraryResponseSchema),
  total: z.number(),
  frameworks: z.array(z.string()),
});
export type ControlListResponse = z.infer<typeof ControlListResponseSchema>;

export const OrganizationAttestationResponseSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  control_id: z.string(),
  status: upperStatus,
  attestation_text: z.string(),
  evidence_description: z.string().nullable().optional(),
  evidence_urls: z.array(z.string()).optional(),
  attested_by: z.string(),
  attested_at: z.string().nullable().optional(),
  reviewed_by: z.string().nullable().optional(),
  reviewed_at: z.string().nullable().optional(),
  expires_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
});
export type OrganizationAttestationResponse = z.infer<typeof OrganizationAttestationResponseSchema>;

// ============================================================================
// NuGuard SBOM Plugin Analysis Schemas (vuln, deps, atlas)
// ============================================================================

// Vulnerability scan (VulnerabilityScannerPlugin)
export const VulnFindingSchema = z.object({
  rule_id: z.string(),
  severity: z.string(),
  title: z.string(),
  description: z.string(),
  file_path: z.string().nullable().optional(),
  line_no: z.number().nullable().optional(),
  code_snippet: z.string().nullable().optional(),
  remediation: z.string().nullable().optional(),
});
export type VulnFinding = z.infer<typeof VulnFindingSchema>;

export const VulnScanResultSchema = z.object({
  status: z.string(),
  message: z.string(),
  findings: z.array(VulnFindingSchema).default([]),
});
export type VulnScanResult = z.infer<typeof VulnScanResultSchema>;

// Dependency analysis (DependencyAnalyzerPlugin + OSV enrichment)
export const CveDetailSchema = z.object({
  id: z.string(),
  severity: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
  published: z.string().nullable().optional(),
  aliases: z.array(z.string()).default([]),
  fixed_version: z.string().nullable().optional(),
  // False when the package's version spec already clears this CVE. Defaults to
  // true so older results without the field are treated as active.
  active: z.boolean().default(true),
});
export type CveDetail = z.infer<typeof CveDetailSchema>;

export const DepPackageSchema = z.object({
  name: z.string(),
  version_spec: z.string().nullable().optional(),
  purl: z.string().nullable().optional(),
  group: z.string().nullable().optional(),
  source_file: z.string().nullable().optional(),
  cves: z.array(CveDetailSchema).default([]),
  recommended_version: z.string().nullable().optional(),
  cve_summary: z.string().nullable().optional(),
  origin: z.string().default("manifest"),  // "manifest" | "deep_scan" (Checkov/Trivy source-tree scan, no lockfile entry)
});
export type DepPackage = z.infer<typeof DepPackageSchema>;

export const DepAnalysisResultSchema = z.object({
  status: z.string(),
  message: z.string(),
  packages: z.array(DepPackageSchema).default([]),
});
export type DepAnalysisResult = z.infer<typeof DepAnalysisResultSchema>;

// MITRE Atlas mappings (AtlasAnnotatorPlugin)
export const AtlasTechniqueSchema = z.object({
  technique_id: z.string(),
  tactic_name: z.string(),
  tactic_id: z.string().nullable().optional(),
  // Plugin returns a string ("HIGH" | "MEDIUM" | "LOW") not a number
  confidence: z.union([z.string(), z.number()]),
  technique_name: z.string().optional(),
  atlas_url: z.string().optional(),
  basis: z.string().optional(),
  mitigations: z.array(z.record(z.unknown())).optional(),
}).passthrough();
export type AtlasTechnique = z.infer<typeof AtlasTechniqueSchema>;

export const AtlasFindingSchema = z.object({
  rule_id: z.string(),
  severity: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  affected: z.array(z.string()).default([]),
  remediation: z.string().nullable().optional(),
  atlas: z.object({ techniques: z.array(AtlasTechniqueSchema), atlas_version: z.string().optional() }).passthrough(),
});
export type AtlasFinding = z.infer<typeof AtlasFindingSchema>;

export const AtlasMapResultSchema = z.object({
  status: z.string(),
  message: z.string(),
  findings: z.array(AtlasFindingSchema).default([]),
});
export type AtlasMapResult = z.infer<typeof AtlasMapResultSchema>;

// ---------------------------------------------------------------------------
// NGA Rules analysis schemas
// ---------------------------------------------------------------------------

export const NgaFindingSchema = z.object({
  finding_id: z.string(),
  title: z.string(),
  severity: z.string(),
  description: z.string(),
  affected_component: z.string().nullable().optional(),
  remediation: z.string().nullable().optional(),
  evidence: z.string().nullable().optional(),
  references: z.array(z.string()).default([]),
  sbom_path: z.array(z.string()).default([]),
  sbom_path_descriptions: z.array(z.string()).default([]),
  owasp_asi_ref: z.string().nullable().optional(),
  owasp_llm_ref: z.string().nullable().optional(),
});
export type NgaFinding = z.infer<typeof NgaFindingSchema>;

export const NgaRulesResultSchema = z.object({
  status: z.string(),
  findings: z.array(NgaFindingSchema).default([]),
  tool_status: z.record(z.unknown()).nullable().optional(),
  ran_at: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});
export type NgaRulesResult = z.infer<typeof NgaRulesResultSchema>;

// NGA-SC supply chain rules — identical response shape, distinct type for clarity
export const NgaScRulesResultSchema = NgaRulesResultSchema;
export type NgaScRulesResult = NgaRulesResult;

// ---------------------------------------------------------------------------
// Deep Scan (Checkov / Semgrep / Trivy against the source tree)
// ---------------------------------------------------------------------------

export const DeepScanFindingSchema = NgaFindingSchema.extend({
  tool: z.string().nullable().optional(),        // "checkov" | "trivy" | "semgrep"
  check_type: z.string().nullable().optional(),   // "code" | "iac"
  occurrence_count: z.number().nullable().optional(),  // set when >1 identical (tool, rule) findings were deduped into this row
});
export type DeepScanFinding = z.infer<typeof DeepScanFindingSchema>;

export const DeepScanResultSchema = NgaRulesResultSchema.extend({
  findings: z.array(DeepScanFindingSchema).default([]),
});
export type DeepScanResult = z.infer<typeof DeepScanResultSchema>;

const MitreAtlasTechniqueRefSchema = z.object({
  technique_id: z.string(),
  technique_name: z.string().optional(),
  atlas_url: z.string().optional(),
});
export type MitreAtlasTechniqueRef = z.infer<typeof MitreAtlasTechniqueRefSchema>;

/** Merged view of a single NuGuard rule with its pass/fail status for a given scan. */
export const NuGuardRuleStatusSchema = z.object({
  rule_id: z.string(),
  title: z.string(),
  severity: z.string(),
  description: z.string().nullable().optional(),
  remediation: z.string().nullable().optional(),
  references: z.array(z.string()).default([]),
  owasp_asi_ref: z.string().nullable().optional(),
  owasp_llm_ref: z.string().nullable().optional(),
  mitre_atlas_techniques: z.array(MitreAtlasTechniqueRefSchema).nullable().optional(),
  status: z.enum(['VIOLATED', 'PASSED']),
  finding: NgaFindingSchema.nullable().optional(),
  source: z.enum(['nga', 'nga-sc']).default('nga'),
});
export type NuGuardRuleStatus = z.infer<typeof NuGuardRuleStatusSchema>;

// ---------------------------------------------------------------------------
// Cognitive Policy Behavior analysis schemas
// ---------------------------------------------------------------------------

export const BehaviorFindingSchema = z.object({
  severity: z.string(),
  title: z.string().nullable().optional(),
  description: z.string(),
  recommendation: z.string().nullable().optional(),
  remediation: z.string().nullable().optional(),  // nuguard Finding model uses this field name
  scenario_type: z.string().nullable().optional(),
});
export type BehaviorFinding = z.infer<typeof BehaviorFindingSchema>;

export const CognitivePolicyBehaviorResultSchema = z.object({
  status: z.string(),
  static_findings: z.array(BehaviorFindingSchema).default([]),
  cognitive_policy_text: z.string().nullable().optional(),
  policy_id: z.string().nullable().optional(),
  scan_outcome: z.string().nullable().optional(),
  overall_risk_score: z.number().nullable().optional(),
  intent_alignment_score: z.number().nullable().optional(),
  ran_at: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});
export type CognitivePolicyBehaviorResult = z.infer<typeof CognitivePolicyBehaviorResultSchema>;

// ---------------------------------------------------------------------------
// Re-run static analysis response schema
// ---------------------------------------------------------------------------

export const StaticAnalysisRunResultSchema = z.object({
  status: z.string(),
  scan_id: z.string(),
  nga_rules_status: z.string(),
  cognitive_policy_status: z.string(),
  vuln_status: z.string().nullable().optional(),
  deps_status: z.string().nullable().optional(),
  atlas_status: z.string().nullable().optional(),
  message: z.string().nullable().optional(),
});
export type StaticAnalysisRunResult = z.infer<typeof StaticAnalysisRunResultSchema>;

// ============================================================================
// Dashboard v2 — frozen API contract (drives backend implementation in PR 4)
// ============================================================================
export const DashboardV2TimeRangeSchema = z.enum(['24h', '7d', '30d', 'live', 'custom']);
export type DashboardV2TimeRange = z.infer<typeof DashboardV2TimeRangeSchema>;

export const DashboardV2AppStatusSchema = z.enum(['HEALTHY', 'AT_RISK', 'CRITICAL', 'UNSCANNED']);
export type DashboardV2AppStatus = z.infer<typeof DashboardV2AppStatusSchema>;

const SeverityPairSchema = z.object({
  critical: z.number().int().nonnegative(),
  high: z.number().int().nonnegative(),
});

export const DashboardV2KpisSchema = z.object({
  total_apps: z.number().int().nonnegative(),
  total_apps_delta_week: z.number().int(),
  avg_risk_score: z.number().min(0).max(100),
  avg_risk_score_delta_week: z.number(),
  coverage_pct: z.number().min(0).max(100),
  applied_policies: z.number().int().nonnegative(),
  validation_findings: SeverityPairSchema,
  vulnerabilities: SeverityPairSchema,
});
export type DashboardV2Kpis = z.infer<typeof DashboardV2KpisSchema>;

export const DashboardV2TimeseriesPointSchema = z.object({
  bucket: z.string(),
  scans: z.number().int().nonnegative(),
  critical: z.number().int().nonnegative(),
  high: z.number().int().nonnegative(),
  medium: z.number().int().nonnegative(),
  low: z.number().int().nonnegative(),
});
export type DashboardV2TimeseriesPoint = z.infer<typeof DashboardV2TimeseriesPointSchema>;

export const RiskBandTrendPointSchema = z.object({
  bucket:         z.string(),
  critical_avg:   z.number().default(0),
  critical_count: z.number().int().nonnegative().default(0),
  high_avg:       z.number().default(0),
  high_count:     z.number().int().nonnegative().default(0),
  medium_avg:     z.number().default(0),
  medium_count:   z.number().int().nonnegative().default(0),
  low_avg:        z.number().default(0),
  low_count:      z.number().int().nonnegative().default(0),
  // Flat mean of all raw score signals across all apps for this day.
  // Matches the avg_risk_score KPI formula — the "today" bar cross-validates the Risk Score card.
  overall_avg:    z.number().default(0),
});
export type RiskBandTrendPoint = z.infer<typeof RiskBandTrendPointSchema>;

export const SbomComponentItemSchema = z.object({
  name: z.string(),
  node_type: z.string().nullable().optional(),
  severity: z.string().nullable().optional(),
  cve_count: z.number().int().nonnegative().default(0),
});
export type SbomComponentItem = z.infer<typeof SbomComponentItemSchema>;

export const DashboardV2StaticPillarSchema = z.object({
  sbom_components: z.number().int().nonnegative(),
  vulnerabilities: SeverityPairSchema,
  total_vulnerabilities: z.number().int().nonnegative().default(0),
  risk_score: z.number().min(0).max(100).default(0),
  top_components: z.array(SbomComponentItemSchema).default([]),
});
export type DashboardV2StaticPillar = z.infer<typeof DashboardV2StaticPillarSchema>;

export const DashboardV2PolicyViolationTypeSchema = z.object({
  label: z.string(),
  count: z.number().int().nonnegative(),
  severity: z.string().default('MEDIUM'),
  framework: z.string().nullable().optional(),
  control_id: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});
export type DashboardV2PolicyViolationType = z.infer<typeof DashboardV2PolicyViolationTypeSchema>;

export const DashboardV2PolicyPillarSchema = z.object({
  applied: z.number().int().nonnegative(),
  violations: SeverityPairSchema,
  violations_total: z.number().int().nonnegative().default(0),
  risk_score: z.number().min(0).max(100).default(0),
  violation_types: z.array(DashboardV2PolicyViolationTypeSchema).default([]),
});
export type DashboardV2PolicyPillar = z.infer<typeof DashboardV2PolicyPillarSchema>;

export const DashboardV2RuntimePillarSchema = z.object({
  validations_total: z.number().int().nonnegative(),
  behavior_findings: SeverityPairSchema,
  behavior_risk_score: z.number().min(0).max(100).default(0),
  redteam: SeverityPairSchema,
  redteam_risk_score: z.number().min(0).max(100),
});
export type DashboardV2RuntimePillar = z.infer<typeof DashboardV2RuntimePillarSchema>;

export const DashboardV2PillarsSchema = z.object({
  static: DashboardV2StaticPillarSchema,
  policy: DashboardV2PolicyPillarSchema,
  runtime: DashboardV2RuntimePillarSchema,
});
export type DashboardV2Pillars = z.infer<typeof DashboardV2PillarsSchema>;

export const DashboardV2AppRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: DashboardV2AppStatusSchema,
  owner: z.string().nullable(),
  risk_score: z.number().min(0).max(100),
  coverage_pct: z.number().min(0).max(100),
});
export type DashboardV2AppRow = z.infer<typeof DashboardV2AppRowSchema>;

export const DashboardV2ResponseSchema = z.object({
  kpis: DashboardV2KpisSchema,
  timeseries: z.array(DashboardV2TimeseriesPointSchema).default([]),
  risk_band_trend: z.array(RiskBandTrendPointSchema).default([]),
  pillars: DashboardV2PillarsSchema,
  applications: z.array(DashboardV2AppRowSchema).default([]),
  time_range: DashboardV2TimeRangeSchema,
  data_as_of: z.string(),
});
export type DashboardV2Response = z.infer<typeof DashboardV2ResponseSchema>;

// Drill-down payloads (lazy-loaded from drawers)
export const DashboardV2SbomTopRowSchema = z.object({
  component: z.string(),
  version: z.string().nullable().optional(),
  cve: z.string().nullable().optional(),
  severity: SeveritySchema.nullable().optional(),
  node_type: z.string().nullable().optional(),
  cve_count: z.number().int().nonnegative().default(0),
});
export type DashboardV2SbomTopRow = z.infer<typeof DashboardV2SbomTopRowSchema>;

export const DashboardV2RedTeamRowSchema = z.object({
  attack: z.string(),
  category: z.string(),
  severity: SeveritySchema,
  succeeded_at: z.string().nullable(),
  source: z.string().default('Red-Team'),
});
export type DashboardV2RedTeamRow = z.infer<typeof DashboardV2RedTeamRowSchema>;

// ============================================================================
// Risk Catalog Schemas (phase 1 — read-time aggregation of validation_findings)
// Mirror the Pydantic response models in data_service/routes/risk_catalog.py.
// ============================================================================

export const RiskCatalogStatusSchema = z.enum(['REMEDIATION_SUGGESTED', 'NEEDS_REMEDIATION']);
export type RiskCatalogStatus = z.infer<typeof RiskCatalogStatusSchema>;

export const RemediationCoverageItemSchema = z.object({
  remediation_text: z.string(),
  applies_to_count: z.number().int().nonnegative(),
  total_count: z.number().int().nonnegative(),
});
export type RemediationCoverageItem = z.infer<typeof RemediationCoverageItemSchema>;

export const RemediationArtefactItemSchema = z.object({
  id: z.string(),
  component: z.string(),
  component_type: z.string().nullable().optional(),
  artefact_type: z.string(),
  priority: z.string(),
  rationale: z.string().nullable().optional(),
  applies_to_count: z.number().int().nonnegative(),
  total_count: z.number().int().nonnegative(),
});
export type RemediationArtefactItem = z.infer<typeof RemediationArtefactItemSchema>;

export const ContributingFindingSchema = z.object({
  id: z.string(),
  finding_id: z.string(),
  run_id: z.string(),
  run_type: z.string(),
  severity: SeveritySchema,
  title: z.string(),
  description: z.string().nullable().optional(),
  affected_component: z.string().nullable().optional(),
  remediation: z.string().nullable().optional(),
  evidence: z.string().nullable().optional(),
  goal_type: z.string().nullable().optional(),
  scenario_label: z.string(),
  scenario: z.string().nullable().optional(),
  technique: z.string().nullable().optional(),
  attack_steps: z.unknown().nullable().optional(),
  created_at: z.string().nullable().optional(),
});
export type ContributingFinding = z.infer<typeof ContributingFindingSchema>;

export const ContributingRunSchema = z.object({
  run_id: z.string(),
  run_type: z.string(),
  finding_count: z.number().int().nonnegative(),
  first_seen: z.string().nullable().optional(),
  last_seen: z.string().nullable().optional(),
});
export type ContributingRun = z.infer<typeof ContributingRunSchema>;

export const RiskCatalogRecordSummarySchema = z.object({
  id: z.string(),
  application_id: z.string().nullable().optional(),
  application_name: z.string().nullable().optional(),
  title: z.string(),
  severity: SeveritySchema,
  status: RiskCatalogStatusSchema,
  goal_type: z.string().nullable().optional(),
  scenario_label: z.string(),
  owasp_llm_ref: z.string().nullable().optional(),
  owasp_asi_ref: z.string().nullable().optional(),
  mitre_atlas_technique: z.string().nullable().optional(),
  finding_count: z.number().int().nonnegative(),
  run_count: z.number().int().nonnegative(),
  run_types: z.array(z.string()).default([]),
  remediation_covered: z.number().int().nonnegative(),
  remediation_total: z.number().int().nonnegative(),
  first_seen: z.string().nullable().optional(),
  last_seen: z.string().nullable().optional(),
});
export type RiskCatalogRecordSummary = z.infer<typeof RiskCatalogRecordSummarySchema>;

export const RiskCatalogListResponseSchema = z.object({
  records: z.array(RiskCatalogRecordSummarySchema).default([]),
  total: z.number().int().nonnegative(),
});
export type RiskCatalogListResponse = z.infer<typeof RiskCatalogListResponseSchema>;

export const RiskCatalogRecordDetailSchema = RiskCatalogRecordSummarySchema.extend({
  description: z.string().nullable().optional(),
  findings: z.array(ContributingFindingSchema).default([]),
  runs: z.array(ContributingRunSchema).default([]),
  remediation_coverage: z.array(RemediationCoverageItemSchema).default([]),
  remediation_artefacts: z.array(RemediationArtefactItemSchema).default([]),
});
export type RiskCatalogRecordDetail = z.infer<typeof RiskCatalogRecordDetailSchema>;

// ============================================================================
// Licensing Schemas
// ============================================================================

export const PlanTierSchema = z.enum(['TRIAL', 'STARTER', 'PRO', 'ENTERPRISE']);
export type PlanTier = z.infer<typeof PlanTierSchema>;

export const SubscriptionStatusSchema = z.enum(['TRIAL', 'ACTIVE', 'SUSPENDED', 'CANCELLED', 'EXPIRED']);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const FeatureTypeSchema = z.enum([
  'SBOM', 'COGNITIVE_POLICY', 'STATIC_ANALYSIS', 'POLICY_ASSESSMENT', 'BEHAVIOR_VALIDATION',
  'RED_TEAM', 'REPORT_GENERATION', 'PURCHASE', 'ALLOCATION', 'REFUND',
]);
export type FeatureType = z.infer<typeof FeatureTypeSchema>;

export const BillingTierSchema = z.enum(['FREE', 'BASE', 'DISCOUNT', 'PAYG']);
export type BillingTier = z.infer<typeof BillingTierSchema>;

export const SubscriptionPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: PlanTierSchema,
  monthly_credits: z.number().nullable().optional(),
  monthly_price_cents: z.number(),
  base_credit_price_cents: z.number(),
  payg_credit_price_cents: z.number(),
  discount_pct: z.number(),
  features_json: z.record(z.unknown()).nullable().optional(),
  is_active: z.boolean(),
});
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export const TenantSubscriptionSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  plan_id: z.string(),
  plan_name: z.string().nullable().optional(),
  plan_tier: z.string().nullable().optional(),
  status: SubscriptionStatusSchema,
  credits_balance: z.number(),
  credits_included: z.number(),
  payg_credits_accrued: z.number(),
  plan_discount_pct: z.number(),
  billing_period_start: z.string().nullable().optional(),
  billing_period_end: z.string().nullable().optional(),
  stripe_customer_id: z.string().nullable().optional(),
  stripe_payment_method_id: z.string().nullable().optional(),
  auto_recharge_enabled: z.boolean().optional(),
  auto_recharge_threshold_credits: z.number().optional(),
  auto_recharge_amount_credits: z.number().optional(),
  overage_billing_enabled: z.boolean().optional(),
  pending_3ds_payment_intent_id: z.string().nullable().optional(),
  billing_interval: z.enum(['monthly', 'annual']).nullable().optional(),
  subscription_credits_remaining: z.number().optional(),
  subscription_credits_period_end: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
export type TenantSubscription = z.infer<typeof TenantSubscriptionSchema>;

export const CreditTransactionSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  user_id: z.string().nullable().optional(),
  scan_id: z.string().nullable().optional(),
  feature_type: FeatureTypeSchema,
  input_tokens: z.number(),
  output_tokens: z.number(),
  loc: z.number().nullable().optional(),
  policy_count: z.number().nullable().optional(),
  application_name: z.string().nullable().optional(),
  credit_rate_per_1k_loc: z.number().nullable().optional(),
  credits_delta: z.number(),
  unit_price_cents: z.number(),
  billing_tier: BillingTierSchema,
  balance_after: z.number(),
  description: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
});
export type CreditTransaction = z.infer<typeof CreditTransactionSchema>;

export const UsageSummarySchema = z.object({
  period_label: z.string(),
  period_start: z.string(),
  period_end: z.string(),
  total_credits: z.number(),
  total_input_tokens: z.number(),
  total_output_tokens: z.number(),
  by_feature: z.record(z.number()),
  by_feature_tokens: z.record(z.object({
    input_tokens: z.number(),
    output_tokens: z.number(),
  })).optional().default({}),
});
export type UsageSummary = z.infer<typeof UsageSummarySchema>;

export const UsageByDimensionSchema = z.object({
  dimension_id: z.string(),
  dimension_label: z.string(),
  total_credits: z.number(),
  total_input_tokens: z.number(),
  total_output_tokens: z.number(),
  by_feature: z.record(z.number()),
});
export type UsageByDimension = z.infer<typeof UsageByDimensionSchema>;

export const QuotaStatusSchema = z.object({
  tenant_id: z.string(),
  credits_balance: z.number(),
  subscription_status: z.string(),
  blocked: z.boolean().optional().default(false),
  block_reason: z.string().nullable().optional(),
});
export type QuotaStatus = z.infer<typeof QuotaStatusSchema>;

export const LicensingNotificationAudienceSchema = z.enum(['ADMIN', 'ALL']);
export const LicensingNotificationSeveritySchema = z.enum(['INFO', 'WARNING', 'CRITICAL']);
export const LicensingNotificationCodeSchema = z.enum([
  'LICENSE_EXPIRING', 'LICENSE_EXPIRED', 'CREDITS_80', 'CREDITS_95', 'CREDITS_100',
]);

export const LicensingNotificationSchema = z.object({
  id: z.string(),
  audience: LicensingNotificationAudienceSchema,
  severity: LicensingNotificationSeveritySchema,
  code: LicensingNotificationCodeSchema,
  message: z.string(),
  dismissible: z.boolean(),
  created_at: z.string().nullable().optional(),
  dismissed_at: z.string().nullable().optional(),
});
export type LicensingNotification = z.infer<typeof LicensingNotificationSchema>;

// ============================================================================
// Validation Helpers
// ============================================================================
export function validate<T>(schema: z.ZodSchema<T>, data: unknown): T {
  return schema.parse(data);
}

/**
 * Safely validate data, returning null on failure
 */
export function safeValidate<T>(schema: z.ZodSchema<T>, data: unknown): T | null {
  const result = schema.safeParse(data);
  return result.success ? result.data : null;
}

/**
 * Validate and return validation errors if any
 */
export function validateWithErrors<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { data: T; errors: null } | { data: null; errors: z.ZodError } {
  const result = schema.safeParse(data);
  if (result.success) {
    return { data: result.data, errors: null };
  }
  return { data: null, errors: result.error };
}
